import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {Button} from 'reactstrap';
import ButtonGroup from '@mui/material/ButtonGroup';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './web-page.reducer';

export const WebPage = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const webPageList = useAppSelector(state => state.webPage.entities);
  const loading = useAppSelector(state => state.webPage.loading);
  const totalItems = useAppSelector(state => state.webPage.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="web-page-heading" data-cy="WebPageHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.webPage.home.title">Web Pages</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" onClick={handleSyncList} disabled={loading} variant="outlined" startIcon={<FontAwesomeIcon icon="sync" spin={loading} />}>
            <Translate contentKey="jhipsterSampleApplicationApp.webPage.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Button component={Link} to="/web-page/new" variant="contained" color="primary" id="jh-create-entity" data-cy="entityCreateButton" startIcon={<FontAwesomeIcon icon="plus" />}>
            <Translate contentKey="jhipsterSampleApplicationApp.webPage.home.createLabel">Create new Web Page</Translate>
          </Button>
        </div>
      </h2>
      <div className="table-responsive">
        {webPageList && webPageList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('id')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('code')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.code">Code</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('code')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('enabled')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.enabled">Enabled</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('enabled')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('uriPath')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.uriPath">Uri Path</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('uriPath')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('publishDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.publishDate">Publish Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('publishDate')} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={sort('createdDate')}>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.webPage.createdBy">Created By</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {webPageList.map((webPage, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/web-page/${webPage.id}`} variant="text" size="small">
                        {webPage.id}
                      </Button>
                    </TableCell>
                    <TableCell>{webPage.code}</TableCell>
                    <TableCell>{webPage.enabled ? 'true' : 'false'}</TableCell>
                    <TableCell>{webPage.uriPath}</TableCell>
                    <TableCell>
                      {webPage.publishDate ? <TextFormat type="date" value={webPage.publishDate} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>
                      {webPage.createdDate ? <TextFormat type="date" value={webPage.createdDate} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{webPage.createdBy ? webPage.createdBy.login : ''}</TableCell>
                    <TableCell className="text-end">
                      <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                        <Button component={Link} to={`/web-page/${webPage.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`} color="primary" startIcon={<FontAwesomeIcon icon="pencil-alt" />} data-cy="entityEditButton">
                        </Button>
                        <Button onClick={() => (window.location.href = `/web-page/${webPage.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)} color="error" startIcon={<FontAwesomeIcon icon="trash" />} data-cy="entityDeleteButton">
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.webPage.home.notFound">No Web Pages found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={webPageList && webPageList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default WebPage;
